import { colors } from '@apollo/brand';
import { gql, useQuery } from '@apollo/client';
import {
  Badge,
  CardDescription,
  CardHeader,
  CardTitle,
  Text,
  customColors,
} from '@apollo/orbit';
import moment from 'moment';
import React, { useMemo } from 'react';

import { useStatsVariables } from 'src/app/account/championDashboardPage/useStatsVariables';
import { Icon as IconPlanetWithFlag } from 'src/assets/icon-planet-with-flag.svg';
import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import { LineChart } from 'src/components/charts/LineChart';
import { Loading } from 'src/components/common/loading/Loading';
import { format } from 'src/lib/format';
import {
  RequestsWidgetQuery,
  RequestsWidgetQueryVariables,
  RequestsWidgetQuery_account_statsWindow_queryStats,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

import {
  ChampionDashboardCard,
  ChampionDashboardCardBody,
} from '../ChampionDashboardCard';
import { EmptyLayout } from '../EmptyLayout';

const requestsWidgetQuery = gql<
  RequestsWidgetQuery,
  RequestsWidgetQueryVariables
>`
  query RequestsWidgetQuery(
    $accountId: ID!
    $resolution: Resolution!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
    $filter: AccountQueryStatsFilter
  ) {
    account(id: $accountId) {
      id
      statsWindow(from: $timeFrom, to: $timeTo, resolution: $resolution) {
        queryStats(filter: $filter) {
          timestamp
          metrics {
            totalRequestCount
          }
        }
      }
    }
  }
`;

const DEFAULT_DAYS = 90;
export const RequestsWidget = ({
  accountId,
  maxRangeInDays,
}: {
  accountId: string;
  maxRangeInDays: number;
}) => {
  const days = Math.min(DEFAULT_DAYS, maxRangeInDays);
  const { timeFrom, timeTo, resolution } = useStatsVariables(days);

  const { data, loading } = useQuery(requestsWidgetQuery, {
    variables: { accountId, resolution, timeFrom, timeTo },
  });

  const queryStats = data?.account?.statsWindow?.queryStats;
  const totalRequests = useMemo(
    () =>
      queryStats?.reduce(
        (sum, col) => sum + col.metrics.totalRequestCount,
        0,
      ) ?? 0,
    [queryStats],
  );

  const chartData = useMemo(
    () => [
      {
        colorGradientTop: `${colors.primitives.blue[300]}3C`,
        colorGradientBottom: `${colors.primitives.blue[300]}00`,
        primaryColor: customColors.flowchart.d,
        cursorColor: customColors.brand.accent,
        unit: ' operations',
        data: queryStats ?? [],
        getValue: (
          stats: RequestsWidgetQuery_account_statsWindow_queryStats,
        ) => ({
          timestamp: stats.timestamp,
          value: stats.metrics.totalRequestCount,
        }),
      },
    ],
    [queryStats],
  );

  const xAxisFirstTickValue = useMemo(
    () => (days === DEFAULT_DAYS ? moment(timeFrom).add(3, 'day') : undefined),
    [days, timeFrom],
  );
  return (
    <ChampionDashboardCard className="col-span-full xl:col-span-4">
      <CardHeader>
        <CardTitle>
          Operations <Badge variant="info">{days} days</Badge>
        </CardTitle>
        {totalRequests > 0 && (
          <CardDescription>
            <Text as="span" variant="bodySb" className="text-secondary">
              {format.number(totalRequests, '0[.]0a')}
            </Text>{' '}
            total operations
          </CardDescription>
        )}
      </CardHeader>
      <ChampionDashboardCardBody>
        {loading ? (
          <Loading />
        ) : totalRequests > 0 ? (
          <CursorProvider>
            <LineChart
              stacked
              stacks={chartData}
              className="size-full"
              // pass in a 6 day tick interval to make sure it lines up with the other champion dashboard charts
              tickIntervalMinutes={
                days === DEFAULT_DAYS
                  ? moment.duration(6, 'days').asMinutes()
                  : undefined
              }
              // pass in the first tick value, so that the tick values line up for all the champion dashboard charts
              firstTickValue={xAxisFirstTickValue}
              resolution={resolution}
            />
          </CursorProvider>
        ) : (
          <EmptyLayout
            icon={<IconPlanetWithFlag />}
            title="Start querying your graph"
            body="Build queries in Explorer to fetch data and use Studio’s performance monitoring system to gain insights about the queries being executed against your graph."
            learnMoreHref={DocsRouter.path('Explorer')}
          />
        )}
      </ChampionDashboardCardBody>
    </ChampionDashboardCard>
  );
};
